import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  TextField,
  InputAdornment,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import ImportMultiUsers from "./ImportMultiUsers";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import SearchIcon from "@mui/icons-material/Search";
import {
  useCreateUserMutation,
  useLazyGetUsersQuery,
} from "@/store/api/adminApi";
import {
  useUpdateUserRoleMutation,
  useUpdateUserMutation,
} from "@/store/api/adminApi";
import { useDeleteUserMutation } from "@/store/api/adminApi";
import { useState, useEffect, useCallback, useMemo } from 'react';
import useDebounce from '@/hooks/useDebounce';
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import AddUserForm from "./AddUserForm.js";
import EditUserForm from "./EditUserForm.js";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";

// Validation Schemas
const userSchema = Yup.object().shape({
  classCode: Yup.string(),
  username: Yup.string()
    .min(3, "Username must be at least 3 characters")
    .max(20, "Username must be less than 20 characters")
    .matches(
      /^[a-zA-Z0-9_]+$/,
      "Username can only contain letters, numbers, and underscores"
    )
    .required("Username is required"),
  email: Yup.string().email("Invalid email format").nullable(),
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
  role: Yup.string()
    .oneOf(["student", "teacher", "admin"], "Invalid role")
    .required("Role is required"),
  dob: Yup.date().nullable(),
  phone: Yup.string().matches(/^[0-9\-\+\(\)\s]*$/, "Invalid phone number"),
});

const editUserSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string().email("Invalid email format").nullable(),
  role: Yup.string()
    .oneOf(["student", "teacher", "admin"], "Invalid role")
    .required("Role is required"),
  classCode: Yup.string()
    .matches(
      /^[A-Za-z0-9_-]*$/,
      "Class code can only contain letters, numbers, underscores and dashes"
    )
    .max(50, "Class code is too long"),
});

const UserManagement = () => {
  const [editDialog, setEditDialog] = useState({ open: false, user: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [addDialog, setAddDialog] = useState({ open: false });
  const [importDialog, setImportDialog] = useState({ open: false });
  const [createUser] = useCreateUserMutation();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [getUsers, { data: usersData, isLoading, error }] =
    useLazyGetUsersQuery();
  const [searchTerm, setSearchTerm] = useState("");

  // Apply debounce to search term
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    getUsers({ page, rowsPerPage, search: debouncedSearchTerm });
  }, [page, rowsPerPage, debouncedSearchTerm]);

  const refetchUsers = () => {
    getUsers({ page, rowsPerPage });
  };

  // Formik form for add user
  const addUserForm = useFormik({
    initialValues: {
      username: "",
      email: "",
      firstName: "",
      lastName: "",
      password: "",
      role: "student",
      dob: "",
      phone: "",
      classCode: "",
    },
    validationSchema: userSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        // Add your API call here to create user
        await createUser({
          username: values.username,
          ...(values.email && { email: values.email }),
          firstName: values.firstName,
          lastName: values.lastName,
          password: values.password,
          role: values.role,
          ...(values.dob && { dob: new Date(values.dob).toISOString() }),
          ...(values.phone && { phone: values.phone }),
          ...(values.classCode && { classCode: values.classCode }),
        }).unwrap();

        toast.success("User created successfully");
        setAddDialog({ open: false });
        resetForm();
        // refetchUsers();
      } catch (error) {
        console.error("Failed to create user:", error);
        toast.error(error.data?.message || "Failed to create user");
      }
    },
  });

  const [updateUserRole] = useUpdateUserRoleMutation();
  const [updateUser] = useUpdateUserMutation();
  const [deleteUser] = useDeleteUserMutation();

  // Formik form for edit user
  const editUserForm = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      role: "student",
      username: "",
      classCode: "",
    },
    validationSchema: editUserSchema,
    onSubmit: async (values) => {
      try {
        await updateUser({
          userId: editDialog.user?._id,
          userData: {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email || null,
            role: values.role,
            // Always send classCode so admins can also clear it (empty string).
            classCode: (values.classCode || "").trim(),
          },
        }).unwrap();

        setEditDialog({ open: false, user: null });
        toast.success("User updated successfully");
        // refetchUsers();
      } catch (error) {
        console.error("Failed to update user:", error);
        toast.error(error.data?.message || "Failed to update user");
      }
    },
  });

  const users = useMemo(() => usersData?.data || [], [usersData]);
  const pagination = useMemo(() => usersData?.pagination || {}, [usersData]);

  const handleAddUser = () => {
    setAddDialog({ open: true });
  };

  const handleEditUser = (user) => {
    editUserForm.setValues({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      role: user.role || "student",
      username: user.username || "",
      classCode: user.classId?.classCode || "",
    });
    setEditDialog({ open: true, user });
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteUser(userId).unwrap();
      setDeleteDialog({ open: false, user: null });
      toast.success("User deleted successfully");
    } catch (error) {
      toast.error("Failed to delete user");
      console.error(error);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ color: "#388e3c", fontWeight: "bold" }}
        >
          User informations management
        </Typography>
      </Box>
      <Box
        sx={{
          my: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
          justifyContent: "space-between",
          flexDirection: {
            xs: "column",
            sm: "column",
            md: "row",
          },
        }}
      >
        <TextField
          sx={{ width: {
            xs: "100%",
            sm: "100%",
            md: "50%",
          } }}
          label="Search users"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            startIcon={<FileUploadIcon />}
            onClick={() => setImportDialog({ open: true })}
            variant="outlined"
            color="secondary"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1,
              "&:hover": {
                backgroundColor: "secondary.light",
                borderColor: "secondary.main",
                color: "white",
              },
            }}
          >
            Import Students
          </Button>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddUser}
            variant="contained"
            color="primary"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1,
              boxShadow: 2,
              "&:hover": {
                boxShadow: 4,
                backgroundColor: "primary.dark",
              },
            }}
          >
            Add User
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={refetchUsers}
            variant="outlined"
            color="inherit"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              px: 2.5,
              py: 1,
              borderColor: "grey.300",
              color: "grey.600",
              "&:hover": {
                backgroundColor: "grey.50",
                borderColor: "grey.400",
              },
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Class</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell>Expired Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users?.map((user) => (
              <TableRow key={user._id}>
                <TableCell>
                  {user.name || user.firstName + " " + user.lastName}
                </TableCell>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.classId?.classCode}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={user.role}
                    color={
                      user.role === "admin"
                        ? "error"
                        : user.role === "teacher"
                        ? "warning"
                        : "primary"
                    }
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {new Date(user.createdAt).toLocaleDateString("vi-VN")}
                </TableCell>
                <TableCell>
                  {new Date(user.expiredAt).toLocaleDateString("vi-VN")}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => handleEditUser(user)}
                      sx={{
                        textTransform: "none",
                        borderRadius: 1.5,
                        px: 2,
                        py: 0.5,
                        fontSize: "0.75rem",
                        minWidth: "auto",
                      }}
                    >
                      Edit User
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => setDeleteDialog({ open: true, user })}
                      sx={{
                        textTransform: "none",
                        borderRadius: 1.5,
                        px: 2,
                        py: 0.5,
                        fontSize: "0.75rem",
                        minWidth: "auto",
                        "&:hover": {
                          backgroundColor: "error.light",
                          color: "white",
                        },
                      }}
                    >
                      Delete
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[rowsPerPage]}
        component="div"
        count={pagination.total || 0}
        rowsPerPage={rowsPerPage}
        page={page - 1}
        onPageChange={(event, newPage) => setPage(newPage + 1)}
        onRowsPerPageChange={(event) =>
          setRowsPerPage(parseInt(event.target.value, 10))
        }
      />

      {/* Edit User Dialog */}
      <EditUserForm
        editDialog={editDialog}
        setEditDialog={setEditDialog}
        editUserForm={editUserForm}
      />

      {/* Add User Dialog */}
      <AddUserForm
        addDialog={addDialog}
        setAddDialog={setAddDialog}
        addUserForm={addUserForm}
      />

      <ImportMultiUsers
        open={importDialog.open}
        onClose={() => setImportDialog({ open: false })}
        onSucess={() => {}}
      />

      {/* Delete User Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, user: null })}
        title="Delete User"
        message="Are you sure you want to delete this user?"
        onConfirm={() => handleDeleteUser(deleteDialog.user?._id)}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};
export default UserManagement;
